class GvsDocumentLibrary extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.#initialise();
  }

  disconnectedCallback() {
    this.#destroy();
  }

  #initialise() {
    this.#destroy();

    const buttons = [...this.querySelectorAll('[data-document-filter]')];
    const cards = [...this.querySelectorAll('[data-document-card]')];
    const searchInput = this.querySelector('[data-document-search]');
    const clearSearchButton = this.querySelector('[data-document-search-clear]');
    const resetButton = this.querySelector('[data-document-reset]');
    const emptyState = this.querySelector('[data-document-empty]');
    const status = this.querySelector('[data-document-filter-status]');

    if (!buttons.length || !cards.length) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const availableFilters = new Set(buttons.map((button) => button.dataset.documentFilter));
    const requestedDefault = this.dataset.defaultFilter || 'all';
    let activeFilter = availableFilters.has(requestedDefault) ? requestedDefault : 'all';
    let searchQuery = '';

    const normalise = (value = '') => value
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('en-GB')
      .trim();

    const searchableCards = cards.map((card) => ({
      element: card,
      categories: (card.dataset.categories || '').split(/\s+/).filter(Boolean),
      searchText: normalise(card.dataset.search || card.textContent || ''),
    }));

    const updateLibrary = () => {
      const queryTerms = normalise(searchQuery).split(/\s+/).filter(Boolean);
      let visibleCount = 0;

      searchableCards.forEach(({ element, categories, searchText }) => {
        const categoryMatches = activeFilter === 'all' || categories.includes(activeFilter);
        const searchMatches = queryTerms.length === 0 || queryTerms.every((term) => searchText.includes(term));
        const visible = categoryMatches && searchMatches;

        element.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      buttons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.documentFilter === activeFilter));
      });

      if (clearSearchButton) {
        clearSearchButton.hidden = normalise(searchQuery).length === 0;
      }

      if (emptyState) {
        emptyState.hidden = visibleCount !== 0;
      }

      if (status) {
        const total = cards.length;
        const hasSearch = normalise(searchQuery).length > 0;
        const hasCategoryFilter = activeFilter !== 'all';

        if (!hasSearch && !hasCategoryFilter) {
          status.textContent = `Showing all ${visibleCount} documents.`;
        } else if (hasSearch) {
          status.textContent = `Showing ${visibleCount} of ${total} documents matching “${searchQuery.trim()}”.`;
        } else {
          status.textContent = `Showing ${visibleCount} of ${total} documents.`;
        }
      }

      this.dataset.activeFilter = activeFilter;
      this.toggleAttribute('data-has-search', normalise(searchQuery).length > 0);
    };

    const resetLibrary = ({ focusSearch = false } = {}) => {
      activeFilter = 'all';
      searchQuery = '';
      if (searchInput) searchInput.value = '';
      updateLibrary();
      if (focusSearch && searchInput) searchInput.focus();
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const requestedFilter = button.dataset.documentFilter || 'all';
        activeFilter = availableFilters.has(requestedFilter) ? requestedFilter : 'all';
        updateLibrary();
      }, { signal });
    });

    if (searchInput) {
      searchInput.addEventListener('input', () => {
        searchQuery = searchInput.value;
        updateLibrary();
      }, { signal });

      searchInput.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !searchInput.value) return;
        searchQuery = '';
        searchInput.value = '';
        updateLibrary();
      }, { signal });
    }

    clearSearchButton?.addEventListener('click', () => {
      searchQuery = '';
      if (searchInput) {
        searchInput.value = '';
        searchInput.focus();
      }
      updateLibrary();
    }, { signal });

    resetButton?.addEventListener('click', () => resetLibrary({ focusSearch: true }), { signal });

    this.setAttribute('data-ready', '');
    updateLibrary();
  }

  #destroy() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.removeAttribute('data-ready');
    this.removeAttribute('data-has-search');
  }
}

if (!customElements.get('gvs-document-library')) {
  customElements.define('gvs-document-library', GvsDocumentLibrary);
}
